import React, { useEffect, useState } from 'react';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { FacilityDetailsForm } from './FacilityForm/FacilityDetailsForm';
import { AdminConfirmation } from './FacilityForm/AdminConfirmation';
import { AdminInviteForm } from './FacilityForm/AdminInviteForm';
import { AdminTypeSelection } from './FacilityForm/AdminTypeSelection';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../images/icons/update_sw_success.svg';
import { ReactComponent as FailedToAddUserIcon } from '../../../../images/icons/adding_user_failed.svg';
import { ReactComponent as FailedToAddFacilityIcon } from '../../../../images/icons/facility_add_failed.svg';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  CreateLocationData,
  CreateParentFacilityData,
} from '../../../../store/facility/facilityTypes';
import {
  addParentFacilityAdmin,
  createParentFacility,
  setAddParentFacilityAdminStatus,
} from '../../../../store/facility/actionCreators';
import { Box, CircularProgress } from '@mui/material';
import { FACILITY_ROLE } from '../../../../shared/constants';

const STEPS = {
  FACILITY_DETAILS: 'facility_details',
  ADMIN_CONFIRMATION: 'admin_confirmation',
  ADMIN_TYPE_SELECTION: 'admin_type_selection',
  ADMIN_INVITE: 'admin_invite',
  SUCCESS: 'success',
  ERROR: 'error',
};

const AddFacilityController = ({
  onBack,
  onCancel,
  onSuccess,
}: {
  onBack: () => void;
  onCancel: () => void;
  onSuccess?: () => void;
}) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(STEPS.FACILITY_DETAILS);
  const [parentFacilityId, setParentFacilityId] = useState<number | null>(null);
  const { addParentFacilityAdminStatus, isLoading } = useSelector(
    (state: RootState) => state.facility,
  );

  const handleFacilitySubmit = async (values: CreateParentFacilityData) => {
    try {
      const facilityId = (await dispatch(
        createParentFacility({
          ...values,
          country: 'United States', // Currently hardcoded to US
        }),
      )) as unknown as number;
      // Only proceed if we got a valid parent facility ID
      if (facilityId) {
        setParentFacilityId(facilityId);
        setCurrentStep(STEPS.ADMIN_CONFIRMATION);
      } else {
        setCurrentStep(STEPS.ERROR);
      }
    } catch (error) {
      setCurrentStep(STEPS.ERROR);
    }
  };

  const handleAdminConfirmation = (wantAdmin: boolean) => {
    if (wantAdmin) {
      setCurrentStep(STEPS.ADMIN_TYPE_SELECTION);
    } else {
      setParentFacilityId(null);
      setCurrentStep(STEPS.SUCCESS);
    }
  };

  const handleAdminTypeSelection = (isNewAdmin: boolean) => {
    if (isNewAdmin) {
      setCurrentStep(STEPS.ADMIN_INVITE);
    }
  };

  const handleAdminInvite = (values: {
    first_name: string;
    last_name: string;
    email: string;
  }) => {
    if (!parentFacilityId) return;

    dispatch(
      addParentFacilityAdmin(parentFacilityId, {
        ...values,
        parent_facility_role_id: String(FACILITY_ROLE.FACILITY_ADMIN),
      }),
    );
  };

  useEffect(() => {
    if (addParentFacilityAdminStatus !== null) {
      setCurrentStep(
        addParentFacilityAdminStatus ? STEPS.SUCCESS : STEPS.ERROR,
      );
    }
  }, [addParentFacilityAdminStatus, dispatch]);

  const renderStep = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      );
    }
    switch (currentStep) {
      case STEPS.FACILITY_DETAILS:
        return (
          <FacilityDetailsForm
            onSubmit={
              handleFacilitySubmit as (
                values: CreateLocationData | CreateParentFacilityData,
              ) => void
            }
            onCancel={onBack}
          />
        );
      case STEPS.ADMIN_CONFIRMATION:
        return (
          <AdminConfirmation
            onNext={handleAdminConfirmation}
            onBack={onCancel}
          />
        );
      case STEPS.ADMIN_TYPE_SELECTION:
        return (
          <AdminTypeSelection
            onNext={handleAdminTypeSelection}
            onBack={() => setCurrentStep(STEPS.ADMIN_CONFIRMATION)}
          />
        );
      case STEPS.ADMIN_INVITE:
        return (
          <AdminInviteForm
            onSubmit={handleAdminInvite}
            onBack={() => setCurrentStep(STEPS.ADMIN_TYPE_SELECTION)}
          />
        );
      case STEPS.SUCCESS:
        return (
          <StatusMessage
            icon={<UpdateSwSuccessIcon />}
            title="Added successfully!"
            onClose={() => {
              dispatch(setAddParentFacilityAdminStatus(null));
              if (onSuccess) {
                onSuccess();
              } else {
                onCancel();
              }
            }}
          />
        );
      case STEPS.ERROR:
        return (
          <StatusMessage
            icon={
              addParentFacilityAdminStatus === false ? (
                <FailedToAddUserIcon />
              ) : (
                <FailedToAddFacilityIcon />
              )
            }
            title={
              addParentFacilityAdminStatus === false
                ? "Facility admin didn't save"
                : "Facility didn't save"
            }
            message="Please try again or come back later."
            onClose={() => {
              if (addParentFacilityAdminStatus === false) {
                setCurrentStep(STEPS.ADMIN_INVITE);
                dispatch(setAddParentFacilityAdminStatus(null));
              } else {
                setCurrentStep(STEPS.FACILITY_DETAILS);
              }
            }}
            primaryButtonText="Try again"
          />
        );
      default:
        return null;
    }
  };

  return renderStep();
};

export default AddFacilityController;
